import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Newspaper, Calendar, Tag, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  release: 'bg-primary/10 text-primary',
  regulation: 'bg-amber-500/10 text-amber-600',
  feature: 'bg-accent/10 text-accent',
  partnership: 'bg-cyan-500/10 text-cyan-600',
  maintenance: 'bg-muted text-muted-foreground',
};

const categoryLabels = {
  release: 'Release',
  regulation: 'Regulation',
  feature: 'Feature',
  partnership: 'Partnership',
  maintenance: 'Maintenance',
};

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/news?published_only=true`).then(r => { setNews(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4"><div className="h-8 w-40 bg-muted rounded animate-pulse" />
      {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />)}
    </div>
  );

  return (
    <div data-testid="news-page" className="space-y-6 max-w-4xl mx-auto">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <Newspaper className="w-6 h-6 text-accent" /> News & Updates
        </h1>
      </div>

      {news.length === 0 ? (
        <Card className="p-12 text-center" data-testid="no-news">
          <Newspaper className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">No news articles yet</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {news.map((item, i) => (
            <Card
              key={item.id}
              className={`p-5 transition-all duration-300 hover:shadow-md cursor-pointer animate-fade-in stagger-${(i % 4) + 1}`}
              onClick={() => setExpanded(expanded === item.id ? null : item.id)}
              data-testid={`news-item-${item.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${categoryColors[item.category] || categoryColors.maintenance}`}>
                      <Tag className="w-2.5 h-2.5" />
                      {categoryLabels[item.category] || item.category}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(item.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <h3 className="font-heading font-semibold text-base mb-1">{item.title}</h3>
                  <p className={`text-sm text-muted-foreground leading-relaxed ${expanded === item.id ? '' : 'line-clamp-2'}`}>
                    {item.content}
                  </p>
                </div>
                <ChevronRight className={`w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 transition-transform ${expanded === item.id ? 'rotate-90' : ''}`} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
